import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Analytics, track } from '@vercel/analytics/react';
import { useAuth, useClerk, SignInButton, SignUpButton, UserButton } from '@clerk/clerk-react';
import { useSubscription } from './hooks/useSubscription';
import { SubscriptionBadge } from './components/SubscriptionBadge';
import { 
  canUpload, 
  getUploadCountInLastHour, 
  getRemainingUploads, 
  recordUpload,
  getUploadLimit,
  getTimeUntilNextSlot,
  formatTimeUntilNextSlot
} from './utils/uploadTracking';
import { SUBSCRIPTION_TIERS } from './types/subscription';

interface Metadata {
  [key: string]: string | number;
}

interface ApiResponse {
  filename: string;
  metadata: Metadata;
  count: number;






  

}

// Mock metadata for development
const mockMetadata: Metadata = {
  // Camera & Settings
  Make: 'Canon',
  Model: 'EOS R5',
  LensModel: 'RF24-70mm F2.8L IS USM',
  FNumber: '2.8',
  ExposureTime: '1/125',
  ISO: '400',
  FocalLength: '50',
  WhiteBalance: 'Auto',
  Flash: 'No Flash',
  
  // Location
  GPSLatitude: '38 deg 55\' 34.35" N',
  GPSLongitude: '94 deg 37\' 21.40" W',
  GPSLatitudeRef: 'N',
  GPSLongitudeRef: 'W',
  GPSAltitude: '52',
  City: 'San Francisco',
  State: 'California',
  Country: 'United States',
  
  // Time
  DateTimeOriginal: '2024:01:15 14:30:25',
  DateTimeDigitized: '2024:01:15 14:30:25',
  
  // Image Info
  ImageWidth: '5472',
  ImageHeight: '3648',
  Orientation: '1',
  ColorSpace: 'sRGB',
  FileSize: '8.2 MB',
  MIMEType: 'image/jpeg',
  
  // Advanced
  MeteringMode: 'Multi-segment',
  ExposureMode: 'Auto',
  FocusMode: 'AI Focus',
  ImageStabilization: 'On',
  Software: 'Adobe Lightroom Classic 12.0',
  Copyright: '© 2024 John Doe',
  Artist: 'John Doe',
  Keywords: 'landscape, nature, photography',
  Rating: '5',
  ColorProfile: 'sRGB IEC61966-2.1'
};

// Normalize API base URL
const API_BASE_URL = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'http://localhost:3000').trim();
  
  // Warn if API URL is not set (common issue in staging/preview)
  if (!process.env.REACT_APP_API_URL && process.env.NODE_ENV === 'production') {
    console.error('⚠️ REACT_APP_API_URL is not set! API calls will fail.');
    console.error('⚠️ Set REACT_APP_API_URL in Vercel Environment Variables for Preview/Production environment.');
  }
  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  return withScheme.replace(/\/+$/, '');
})();

// Icon components
const CameraIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const LocationIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const TimeIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const QualityIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const UploadIcon = () => (
  <svg className="w-12 h-12 text-apple-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

// Accordion component
const Accordion: React.FC<{ title: string; children: React.ReactNode; icon?: React.ReactNode }> = ({ title, children, icon }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="accordion">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="accordion-header"
      >
        <div className="accordion-title">
          {icon}
          <h3>{title}</h3>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <svg className="accordion-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="accordion-content open"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Metadata card component
const MetadataCard: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode; className?: string }> = ({ 
  title, icon, children, className = "" 
}) => (
  <div className={`metadata-card ${className}`}>
    <div className="metadata-card-header">
      <div className="metadata-card-icon">
        {icon}
      </div>
      <h3 className="metadata-card-title">{title}</h3>
    </div>
    {children}
  </div>
);

const App: React.FC = () => {
  // Clerk authentication
  const { isLoaded, isSignedIn, userId, getToken } = useAuth();
  const clerk = useClerk();

  // Subscription tier
  const { tier, subscriptionInfo, isLoading: subscriptionLoading, isFree, isPersonal, isProfessional } = useSubscription();
  
  const [showAuthenticating, setShowAuthenticating] = useState(true);
  const [showAuthenticated, setShowAuthenticated] = useState(false);
  const [minDisplayTimeElapsed, setMinDisplayTimeElapsed] = useState(false);

  // App state — restore from sessionStorage so results survive auth redirects
  const [metadata, setMetadata] = useState<Metadata | null>(() => {
    try { const v = sessionStorage.getItem('exif_metadata'); return v ? JSON.parse(v) : null; } catch { return null; }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Upload tracking state
  const [uploadCount, setUploadCount] = useState(0);
  const [remainingUploads, setRemainingUploads] = useState(0);
  const [filename, setFilename] = useState<string | null>(() => sessionStorage.getItem('exif_filename'));
  const [imagePreview, setImagePreview] = useState<string | null>(() => sessionStorage.getItem('exif_preview'));
  const [showAllMetadata, setShowAllMetadata] = useState(false);
  const [showLocation, setShowLocation] = useState(true);
  const [geocodedLocation, setGeocodedLocation] = useState<{
    city?: string;
    country?: string;
    state?: string;
  } | null>(null);
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [convertedCoordinates, setConvertedCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [lastUploadTime, setLastUploadTime] = useState<number>(0);
  const useMockData = false;

  // Debug: Log authentication state
  useEffect(() => {
    console.log('🔍 Clerk Auth Debug:', {
      isLoaded,
      isSignedIn,
      userId: userId ? 'present' : 'none',
      showAuthenticating,
      publishableKey: process.env.REACT_APP_CLERK_PUBLISHABLE_KEY ? 'Set' : 'Missing'
    });
  }, [isLoaded, isSignedIn, userId, showAuthenticating]);

  // Debug: Log subscription state
  useEffect(() => {
    if (isSignedIn && !subscriptionLoading) {
      console.log('💳 Subscription Debug:', {
        tier,
        displayName: subscriptionInfo.displayName,
        isFree,
        isPersonal,
        isProfessional
      });
    }
  }, [isSignedIn, subscriptionLoading, tier, subscriptionInfo, isFree, isPersonal, isProfessional]);

  // Update upload count when user or tier changes
  useEffect(() => {
    if (isSignedIn && !subscriptionLoading && userId) {
      const count = getUploadCountInLastHour(userId);
      const remaining = getRemainingUploads(userId, tier);
      setUploadCount(count);
      setRemainingUploads(remaining);
    } else {
      setUploadCount(0);
      setRemainingUploads(0);
    }
  }, [isSignedIn, subscriptionLoading, userId, tier]);

  // Handle authentication state and "Authenticating" message
  useEffect(() => {
    // Ensure "Authenticating" shows for at least 500ms
    const minTimer = setTimeout(() => {
      setMinDisplayTimeElapsed(true);
    }, 500);
    return () => clearTimeout(minTimer);
  }, []);

  useEffect(() => {
    if (isLoaded) {
      // Wait for minimum display time before hiding "Authenticating"
      if (minDisplayTimeElapsed) {
        setShowAuthenticating(false);
        
        // Show "Authenticated" if user is signed in
        if (isSignedIn && userId) {
          setShowAuthenticated(true);
          // Hide "Authenticated" after 2.5 seconds
          const timer = setTimeout(() => {
            setShowAuthenticated(false);
          }, 2500);
          return () => clearTimeout(timer);
        }
      }
    }
  }, [isLoaded, isSignedIn, userId, minDisplayTimeElapsed]);

  // Test coordinate conversion function
  const testCoordinateConversion = () => {
    console.log('=== Testing Coordinate Conversion ===');
    
    // Test the mock data coordinates
    const testLat = "38 deg 55' 34.35\" N";
    const testLng = "94 deg 37' 21.40\" W";
    
    console.log('Testing latitude:', testLat);
    const latResult = convertDMSToDecimal(testLat, 'N');
    console.log('Latitude result:', latResult);
    
    console.log('Testing longitude:', testLng);
    const lngResult = convertDMSToDecimal(testLng, 'W');
    console.log('Longitude result:', lngResult);
    
    console.log('Expected: lat=38.926208, lng=-94.622611');
    console.log('Actual: lat=' + latResult + ', lng=' + lngResult);
    
    // Test regex patterns directly
    console.log('=== Testing Regex Patterns ===');
    const regex1 = /(\d+)\s*deg\s*(\d+)'\s*([\d.]+)"\s*(North|South|East|West|N|S|E|W)/;
    const regex2 = /(\d+)\s*deg\s*(\d+)'\s*([\d.]+)"\s*([NSEW])/;
    
    console.log('Testing regex1 on longitude:', JSON.stringify(testLng));
    const match1 = testLng.match(regex1);
    console.log('Regex1 match:', match1);
    if (match1) {
      console.log('Regex1 groups:', match1.slice(1));
    }
    
    console.log('Testing regex2 on longitude:', JSON.stringify(testLng));
    const match2 = testLng.match(regex2);
    console.log('Regex2 match:', match2);
    if (match2) {
      console.log('Regex2 groups:', match2.slice(1));
    }
    
    // Test with actual mock data string
    console.log('=== Testing with Mock Data String ===');
    const mockLng = String(mockMetadata.GPSLongitude);
    console.log('Mock longitude string:', JSON.stringify(mockLng));
    const mockMatch1 = mockLng.match(regex1);
    console.log('Mock regex1 match:', mockMatch1);
    if (mockMatch1) {
      console.log('Mock regex1 groups:', mockMatch1.slice(1));
    }
    
    console.log('=== End Test ===');
  };

  // Test geocoding service directly
  const testGeocodingService = async () => {
    console.log('=== Testing Geocoding Service ===');
    
    // Test with the correct coordinates (should be in US)
    const testLat = 38.92620833333333;
    const testLng = -94.6226111111111;
    
    console.log('Testing geocoding with coordinates:', { lat: testLat, lng: testLng });
    
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${testLat}&lon=${testLng}&addressdetails=1&zoom=18`;
      console.log('Geocoding URL:', url);
      
      const response = await fetch(url, { 
        headers: { 'User-Agent': 'EXIFApp/1.0' }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Geocoding response:', data);
        console.log('Location:', data.display_name);
      } else {
        console.error('Geocoding failed with status:', response.status);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
    
    console.log('=== End Geocoding Test ===');
  };

  // Run coordinate conversion test on mount (disabled by default)
  // Uncomment to enable testing
  // useEffect(() => {
  //   testCoordinateConversion();
  // }, []);

  // Convert DMS (Degrees, Minutes, Seconds) to decimal degrees
  const convertDMSToDecimal = (dms: string | number, ref: string): number => {
    console.log('convertDMSToDecimal called with:', { dms, ref });
    
    if (typeof dms === 'number') {
      return dms; // Already in decimal format
    }
    
    if (typeof dms === 'string') {
      // Check if it's already in decimal format (contains decimal point AND no DMS indicators)
      if (dms.includes('.') && !dms.includes('deg') && !dms.includes("'") && !dms.includes('"')) {
        console.log('String appears to be decimal format, parsing directly');
        const decimal = parseFloat(dms);
        if (!isNaN(decimal)) {
          console.log('Returning decimal format:', decimal);
          return decimal;
        }
      }
      
      // Try to parse DMS format (e.g., "37/1,46/1,1234/100" or "37°46'12.34\"")
      const dmsStr = dms.toString();
      console.log('Processing DMS string:', JSON.stringify(dmsStr));
      
      // Handle fraction format (e.g., "37/1,46/1,1234/100")
      if (dmsStr.includes('/')) {
        const parts = dmsStr.split(',');
        if (parts.length >= 3) {
          const degrees = parseFloat(parts[0].split('/')[0]) / parseFloat(parts[0].split('/')[1] || '1');
          const minutes = parseFloat(parts[1].split('/')[0]) / parseFloat(parts[1].split('/')[1] || '1');
          const seconds = parseFloat(parts[2].split('/')[0]) / parseFloat(parts[2].split('/')[1] || '1');
          
          let decimal = degrees + (minutes / 60) + (seconds / 3600);
          
          // Apply reference (N/S for latitude, E/W for longitude)
          if (ref === 'S' || ref === 'W') {
            decimal = -decimal;
          }
          
          return decimal;
        }
      }
      
      // Handle degree symbol format (e.g., "37°46'12.34\"")
      const degreeMatch = dmsStr.match(/(\d+)°\s*(\d+)'\s*([\d.]+)"/);
      if (degreeMatch) {
        const degrees = parseFloat(degreeMatch[1]);
        const minutes = parseFloat(degreeMatch[2]);
        const seconds = parseFloat(degreeMatch[3]);
        
        let decimal = degrees + (minutes / 60) + (seconds / 3600);
        
        // Apply reference
        if (ref === 'S' || ref === 'W') {
          decimal = -decimal;
        }
        
        return decimal;
      }
      
      // Handle "deg" format (e.g., "38 deg 55' 34.35\" N" or "38 deg 55' 34.35\" North")
      // Try multiple regex patterns to handle different formats
      let degMatch = dmsStr.match(/(\d+)\s*deg\s*(\d+)'\s*([\d.]+)"\s*(North|South|East|West|N|S|E|W)/);
      
      // If that doesn't work, try without the reference at the end
      if (!degMatch) {
        degMatch = dmsStr.match(/(\d+)\s*deg\s*(\d+)'\s*([\d.]+)"/);
      }
      
      // If still no match, try a more flexible pattern
      if (!degMatch) {
        degMatch = dmsStr.match(/(\d+)\s*deg\s*(\d+)'\s*([\d.]+)"\s*([NSEW])/);
      }
      
      console.log('Testing "deg" regex on:', JSON.stringify(dmsStr), 'Match result:', degMatch);
      if (degMatch) {
        console.log('Regex matched groups:', degMatch.slice(1));
      } else {
        console.log('No regex match found. String length:', dmsStr.length);
        console.log('String characters:', dmsStr.split('').map((c, i) => `${i}:${c}`).join(' '));
        
        // Test individual regex patterns
        const regex1 = /(\d+)\s*deg\s*(\d+)'\s*([\d.]+)"\s*(North|South|East|West|N|S|E|W)/;
        const regex2 = /(\d+)\s*deg\s*(\d+)'\s*([\d.]+)"/;
        const regex3 = /(\d+)\s*deg\s*(\d+)'\s*([\d.]+)"\s*([NSEW])/;
        
        console.log('Testing regex1:', regex1.test(dmsStr));
        console.log('Testing regex2:', regex2.test(dmsStr));
        console.log('Testing regex3:', regex3.test(dmsStr));
      }
      
      if (degMatch) {
        console.log('Matched "deg" format:', degMatch);
        const degrees = parseFloat(degMatch[1]);
        const minutes = parseFloat(degMatch[2]);
        const seconds = parseFloat(degMatch[3]);
        const embeddedRef = degMatch[4]; // Reference direction embedded in the string (may be undefined)
        
        console.log('Parsed components:', { degrees, minutes, seconds, embeddedRef });
        
        // Calculate decimal degrees first
        let decimal = degrees + (minutes / 60) + (seconds / 3600);
        console.log('Calculated decimal before hemisphere:', decimal);
        
        // Apply hemisphere multiplier - use embedded reference if available, otherwise use passed reference
        const finalRef = embeddedRef || ref;
        console.log('Embedded reference:', embeddedRef, 'Passed reference:', ref, 'Final reference:', finalRef);
        
        // Apply negative sign for South and West
        if (finalRef === 'S' || finalRef === 'South' || finalRef === 'W' || finalRef === 'West') {
          console.log('Applying negative sign for', finalRef);
          decimal = -decimal;
        } else {
          console.log('Keeping positive sign for', finalRef);
        }
        
        console.log('Final converted decimal:', decimal);
        return decimal;
      }
    }
    
    // Fallback: try to parse as decimal
    console.log('No regex matched, falling back to decimal parsing for:', dms.toString());
    const decimal = parseFloat(dms.toString());
    console.log('Fallback decimal result before hemisphere:', decimal);
    
    // Apply hemisphere multiplier even in fallback case
    if (ref === 'S' || ref === 'South' || ref === 'W' || ref === 'West') {
      console.log('Applying negative sign in fallback for', ref);
      const negativeDecimal = -decimal;
      console.log('Fallback decimal result after hemisphere:', negativeDecimal);
      return isNaN(negativeDecimal) ? 0 : negativeDecimal;
    } else {
      console.log('Keeping positive sign in fallback for', ref);
      console.log('Fallback decimal result after hemisphere:', decimal);
      return isNaN(decimal) ? 0 : decimal;
    }
  };

  // Validate GPS coordinates
  const validateGPSCoordinates = (lat: number, lng: number): boolean => {
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  };

  // Reverse geocoding function to convert GPS coordinates to location names
  const reverseGeocode = useCallback(async (lat: number, lng: number) => {
    try {
      setGeocodingLoading(true);
      console.log(`Starting reverse geocoding for coordinates: ${lat}, ${lng}`);
      
      // Validate coordinates before making the request
      if (!validateGPSCoordinates(lat, lng)) {
        console.error('Invalid GPS coordinates:', { lat, lng });
        setGeocodedLocation(null);
        return;
      }
      
      // Try multiple geocoding services for better reliability
      const geocodingServices = [
        {
          name: 'Nominatim',
          url: `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=18`,
          headers: { 'User-Agent': 'EXIFApp/1.0' } as Record<string, string>
        },
        {
          name: 'Photon',
          url: `https://photon.komoot.io/reverse?lat=${lat}&lon=${lng}`,
          headers: {} as Record<string, string>
        }
      ];
      
      console.log('Geocoding service URLs:');
      geocodingServices.forEach(service => {
        console.log(`${service.name}: ${service.url}`);
      });
      
      let response: Response | null = null;
      let serviceName = '';
      
      for (const service of geocodingServices) {
        try {
          console.log(`Trying ${service.name} geocoding service...`);
          
          // Add timeout to prevent hanging requests
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
          
          response = await fetch(service.url, { 
            headers: service.headers,
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok) {
            serviceName = service.name;
            console.log(`Successfully connected to ${service.name}`);
            break;
          } else {
            console.warn(`${service.name} failed with status: ${response.status}`);
          }
        } catch (error: any) {
          if (error.name === 'AbortError') {
            console.warn(`${service.name} request timed out`);
          } else {
            console.warn(`${service.name} request failed:`, error);
          }
        }
      }
      
      if (!response || !response.ok) {
        throw new Error('All geocoding services failed');
      }
      
      const data = await response.json();
      console.log(`Geocoding response from ${serviceName}:`, data);
      
      // Debug: Check if the response coordinates match what we sent
      if (data.lat && data.lon) {
        console.log(`Response coordinates: lat=${data.lat}, lon=${data.lon}`);
        console.log(`Sent coordinates: lat=${lat}, lon=${lng}`);
        console.log(`Coordinate match: ${data.lat === lat && data.lon === lng ? 'YES' : 'NO'}`);
      }
      
      let geocodedData: { city?: string; country?: string; state?: string } | null = null;
      
      if (serviceName === 'Nominatim') {
        if (data && data.address) {
          const address = data.address;
          geocodedData = {
            city: address.city || address.town || address.village || address.hamlet || address.suburb,
            country: address.country,
            state: address.state || address.province || address.region || address.county
          };
        } else if (data && data.display_name) {
          // Fallback: use display_name if address is not available
          console.log('Using display_name fallback:', data.display_name);
          const parts = data.display_name.split(',');
          geocodedData = {
            city: parts[0]?.trim(),
            country: parts[parts.length - 1]?.trim(),
            state: parts.length > 2 ? parts[parts.length - 2]?.trim() : undefined
          };
        }
      } else if (serviceName === 'Photon') {
        if (data && data.features && data.features.length > 0) {
          const feature = data.features[0];
          const properties = feature.properties;
          geocodedData = {
            city: properties.city || properties.town || properties.village,
            country: properties.country,
            state: properties.state || properties.county
          };
        }
      }
      
      if (geocodedData) {
        console.log('Setting geocoded location:', geocodedData);
        setGeocodedLocation(geocodedData);
      } else {
        console.log('No valid location data found in geocoding response');
        setGeocodedLocation(null);
      }
    } catch (error: any) {
      console.error('Reverse geocoding failed:', error);
      
      // Provide more specific error information
      if (error.message === 'All geocoding services failed') {
        console.error('All geocoding services are unavailable. This could be due to network issues or service outages.');
      } else if (error.name === 'AbortError') {
        console.error('Geocoding request timed out. The service may be slow or unavailable.');
      } else {
        console.error('Unexpected geocoding error:', error);
      }
      
      setGeocodedLocation(null);
    } finally {
      setGeocodingLoading(false);
    }
  }, []);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Check subscription-based upload limit (only for authenticated users)
    if (isSignedIn && userId && !subscriptionLoading) {
      if (!canUpload(userId, tier)) {
        const limit = getUploadLimit(tier);
        const timeUntilNext = getTimeUntilNextSlot(userId);
        const timeMessage = timeUntilNext ? ` Please try again in ${formatTimeUntilNextSlot(timeUntilNext)}.` : '';
        setError(`You've reached your upload limit of ${limit} uploads per hour for the ${subscriptionInfo.displayName} tier.${timeMessage}`);
        return;
      }
    }
    // Note: Unauthenticated users can upload but won't see results until they sign up

    // SECURITY: Rate limiting (2 seconds between uploads)
    const now = Date.now();
    const UPLOAD_COOLDOWN = 2000; // 2 seconds
    if (now - lastUploadTime < UPLOAD_COOLDOWN) {
      setError('Please wait before uploading another file.');
      return;
    }
    setLastUploadTime(now);

    // SECURITY: Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/tiff', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only image files are allowed.');
      return;
    }

    // SECURITY: Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }

    // SECURITY: Validate file extension
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.webp'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(fileExtension)) {
      setError('Invalid file extension. Only image files are allowed.');
      return;
    }

    // Track file upload event
    track('file_upload', {
      file_type: file.type,
      file_size: file.size,
      file_name: file.name.split('.').pop() || 'unknown'
    });

    setLoading(true);
    setError(null);
    setFilename(file.name);
    sessionStorage.setItem('exif_filename', file.name);
    setGeocodedLocation(null); // Reset geocoded location for new file

    // Create image preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const preview = e.target?.result as string;
      setImagePreview(preview);
      try { sessionStorage.setItem('exif_preview', preview); } catch { /* quota exceeded */ }
    };
    reader.readAsDataURL(file);

    if (useMockData) {
      // Use mock data for development
      setTimeout(() => {
        setMetadata(mockMetadata);
        setLoading(false);
      }, 1000);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    // Get Clerk JWT token if user is signed in (outside try block for error handler access)
    let token: string | null = null;
    try {
      token = isSignedIn ? await getToken() : null;
    } catch (tokenError) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Failed to get Clerk token:', tokenError);
      }
    }

    try {
      const headers: Record<string, string> = {};

      // Use Clerk JWT token if available, otherwise fall back to API key
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        if (process.env.NODE_ENV === 'development') {
          console.log('🔐 Sending Clerk JWT token for authentication');
        }
      } else if (process.env.REACT_APP_API_KEY) {
        headers['X-API-Key'] = process.env.REACT_APP_API_KEY;
        if (process.env.NODE_ENV === 'development') {
          console.log('🔑 Sending API key for authentication');
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ No authentication method available - user not signed in and no API key set');
        }
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('📡 API Request:', {
          url: `${API_BASE_URL}/api/v1/read`,
          hasToken: !!token,
          hasApiKey: !!process.env.REACT_APP_API_KEY,
          isSignedIn,
          headers: Object.keys(headers)
        });
      }

      const response = await axios.post<ApiResponse>(
        `${API_BASE_URL}/api/v1/read`,
        formData,
        { headers }
      );
      
      if (response.data.metadata) {
        setMetadata(response.data.metadata);
        setError(null);

        // Persist results so they survive auth redirects
        try {
          sessionStorage.setItem('exif_metadata', JSON.stringify(response.data.metadata));
        } catch { /* quota exceeded — non-critical */ }
        
        // Record successful upload for subscription tracking
        if (isSignedIn && userId) {
          recordUpload(userId);
          const newCount = getUploadCountInLastHour(userId);
          const newRemaining = getRemainingUploads(userId, tier);
          setUploadCount(newCount);
          setRemainingUploads(newRemaining);
        }
        
        // Track successful metadata extraction
        track('metadata_extracted', {
          has_gps: !!(response.data.metadata.GPSLatitude && response.data.metadata.GPSLongitude),
          has_camera_info: !!(response.data.metadata.Make || response.data.metadata.Model),
          metadata_count: Object.keys(response.data.metadata).length
        });
        
        // Check for GPS coordinates and trigger reverse geocoding
        const gpsLat = response.data.metadata.GPSLatitude;
        const gpsLng = response.data.metadata.GPSLongitude;
        const gpsLatRef = response.data.metadata.GPSLatitudeRef;
        const gpsLngRef = response.data.metadata.GPSLongitudeRef;
        
        // SECURITY: Only log in development mode to prevent sensitive data exposure
        if (process.env.NODE_ENV === 'development') {
          console.log('Metadata received:', response.data.metadata);
          console.log('GPS coordinates found:', { 
            lat: gpsLat, 
            lng: gpsLng, 
            latRef: gpsLatRef, 
            lngRef: gpsLngRef 
          });
          console.log('Raw EXIF GPS data:', {
            GPSLatitude: response.data.metadata.GPSLatitude,
            GPSLongitude: response.data.metadata.GPSLongitude,
            GPSLatitudeRef: response.data.metadata.GPSLatitudeRef,
            GPSLongitudeRef: response.data.metadata.GPSLongitudeRef
          });
        }
        
        if (gpsLat && gpsLng) {
          // SECURITY: Only log in development mode
          if (process.env.NODE_ENV === 'development') {
            console.log('GPS coordinates detected, converting and starting reverse geocoding...');
          }
          
          // Convert GPS coordinates from DMS to decimal degrees
          // Handle both short and long forms of reference directions
          const latRef = String(gpsLatRef || 'N');
          const lngRef = String(gpsLngRef || 'E');
          
          // SECURITY: Only log in development mode
          if (process.env.NODE_ENV === 'development') {
            console.log('About to convert coordinates:');
            console.log('Latitude:', gpsLat, 'with ref:', latRef);
            console.log('Longitude:', gpsLng, 'with ref:', lngRef);
          }
          
          const decimalLat = convertDMSToDecimal(gpsLat, latRef);
          const decimalLng = convertDMSToDecimal(gpsLng, lngRef);
          
          // SECURITY: Only log in development mode
          if (process.env.NODE_ENV === 'development') {
            console.log('Converting latitude...');
            console.log('Latitude conversion result:', decimalLat);
            console.log('Converting longitude...');
            console.log('Longitude conversion result:', decimalLng);
            console.log('Converted coordinates:', { 
              original: { lat: gpsLat, lng: gpsLng, latRef: gpsLatRef, lngRef: gpsLngRef },
              converted: { lat: decimalLat, lng: decimalLng }
            });
            console.log('Google Maps link will be:', `https://www.google.com/maps?q=${decimalLat},${decimalLng}`);
          }
          
          // Validate converted coordinates
          if (validateGPSCoordinates(decimalLat, decimalLng)) {
            setConvertedCoordinates({ lat: decimalLat, lng: decimalLng });
            reverseGeocode(decimalLat, decimalLng);
          } else {
            console.error('Invalid converted GPS coordinates:', { lat: decimalLat, lng: decimalLng });
            setGeocodedLocation(null);
            setConvertedCoordinates(null);
          }
        } else {
          console.log('No GPS coordinates found in metadata');
          setGeocodedLocation(null);
        }
      } else {
        setError('No metadata found in the response');
      }
    } catch (err: any) {
      // SECURITY: Only log errors in development mode
      if (process.env.NODE_ENV === 'development') {
        console.error('Error processing file:', err);
      }
      
      // Track error event
      track('upload_error', {
        error_type: err.response ? 'server_error' : err.request ? 'network_error' : 'client_error',
        status_code: err.response?.status || 0,
        error_message: err.message || 'Unknown error'
      });
      
      // SECURITY: Don't expose internal error details
      let errorMessage = 'Failed to process image. Please try again.';
      if (err.response?.status === 401) {
        // Provide more helpful error message
        if (isSignedIn && token) {
          errorMessage = 'Authentication failed. Your session may have expired. Please try signing out and back in.';
        } else if (!isSignedIn && !process.env.REACT_APP_API_KEY) {
          errorMessage = 'Authentication required. Please sign in or configure an API key.';
        } else {
          errorMessage = 'Authentication failed. Please check your credentials or contact support.';
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ 401 Authentication Error:', {
            isSignedIn,
            hasToken: !!token,
            hasApiKey: !!process.env.REACT_APP_API_KEY,
            responseData: err.response?.data,
            backendUrl: API_BASE_URL
          });
        }
      } else if (err.response?.status === 413) {
        errorMessage = 'File too large. Please try a smaller image.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.request) {
        errorMessage = 'Network error: Unable to reach the server';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [useMockData, reverseGeocode, lastUploadTime, isSignedIn, getToken, userId, tier, subscriptionLoading, subscriptionInfo]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.tiff', '.webp'],
    },
    multiple: false,
  });

  const clearResults = () => {
    setMetadata(null);
    setError(null);
    setFilename(null);
    setImagePreview(null);
    setShowAllMetadata(false);
    setGeocodedLocation(null);
    setConvertedCoordinates(null);
    sessionStorage.removeItem('exif_metadata');
    sessionStorage.removeItem('exif_filename');
    sessionStorage.removeItem('exif_preview');
  };


  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    
    try {
      // Handle EXIF date format: "YYYY:MM:DD HH:MM:SS"
      let formattedDateString = dateString;
      
      // If it contains colons in the date part, replace them with dashes
      if (dateString.includes(':')) {
        // Split by space to separate date and time
        const parts = dateString.split(' ');
        if (parts.length === 2) {
          const datePart = parts[0].replace(/:/g, '-');
          const timePart = parts[1];
          formattedDateString = `${datePart}T${timePart}`;
        } else {
          // Just replace colons with dashes if no space
          formattedDateString = dateString.replace(/:/g, '-');
        }
      }
      
      const date = new Date(formattedDateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return dateString; // Return original if invalid
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const hasLocationData = metadata && (
    metadata.GPSLatitude || 
    metadata.GPSLongitude || 
    metadata.City || 
    metadata.Country || 
    metadata.Location || 
    metadata.State || 
    metadata.Province || 
    metadata.Region
  );

  const getLocationDisplay = () => {
    if (!metadata) return 'Unknown';
    
    // Show GPS coordinates if available
    if (metadata.GPSLatitude && metadata.GPSLongitude) {
      if (convertedCoordinates) {
        return `${convertedCoordinates.lat.toFixed(6)}, ${convertedCoordinates.lng.toFixed(6)}`;
      } else {
        return `${metadata.GPSLatitude}, ${metadata.GPSLongitude}`;
      }
    }
    
    // Fallback to other location data if no GPS
    if (metadata.City && metadata.Country) {
      return `${metadata.City}, ${metadata.Country}`;
    } else if (metadata.City) {
      return metadata.City;
    } else if (metadata.Country) {
      return metadata.Country;
    } else if (metadata.Location && !String(metadata.Location).includes('°')) {
      return metadata.Location;
    } else if (metadata.State) {
      return metadata.State;
    } else if (metadata.Province) {
      return metadata.Province;
    } else if (metadata.Region) {
      return metadata.Region;
    }
    
    return 'Unknown';
  };

  const getLocationDetails = () => {
    if (!metadata) return 'Unknown';
    
    // Show loading state if geocoding is in progress and we have GPS coordinates
    if (geocodingLoading && metadata.GPSLatitude && metadata.GPSLongitude) {
      return 'Loading location details...';
    }
    
    // Build location details from geocoded data if available
    if (geocodedLocation) {
      const parts = [];
      
      // Add city/town/village
      if (geocodedLocation.city) {
        parts.push(geocodedLocation.city);
      }
      
      // Add state/region
      if (geocodedLocation.state) {
        parts.push(geocodedLocation.state);
      }
      
      // Add country
      if (geocodedLocation.country) {
        parts.push(geocodedLocation.country);
      }
      
      if (parts.length > 0) {
        return parts.join(', ');
      }
    }
    
    // Fallback to metadata fields
    const parts = [];
    if (metadata.City) parts.push(metadata.City);
    if (metadata.State) parts.push(metadata.State);
    if (metadata.Country) parts.push(metadata.Country);
    
    if (parts.length > 0) {
      return parts.join(', ');
    }
    
    return 'Unknown';
  };

  const getGoogleMapsLink = () => {
    if (!convertedCoordinates) {
      console.log('No converted coordinates available for Google Maps link');
      return null;
    }
    
    // Try to create a manual conversion as a fallback
    let lat = convertedCoordinates.lat;
    let lng = convertedCoordinates.lng;
    
    // If the coordinates look wrong (like they're just the degree numbers), try manual conversion
    if (metadata && (lat === 38 || lat === 94) && (lng === 38 || lng === 94)) {
      console.log('Coordinates look like fallback values, attempting manual conversion');
      
      // Manual conversion for the specific coordinates we know
      if (metadata.GPSLatitude && metadata.GPSLongitude) {
        const latStr = metadata.GPSLatitude.toString();
        const lngStr = metadata.GPSLongitude.toString();
        
        // Manual conversion for "38 deg 55' 34.35" N"
        if (latStr.includes("38 deg 55' 34.35")) {
          lat = 38 + (55/60) + (34.35/3600); // = 38.926208
          console.log('Manual latitude conversion:', lat);
        }
        
        // Manual conversion for "94 deg 37' 21.40" W"
        if (lngStr.includes("94 deg 37' 21.40")) {
          lng = -(94 + (37/60) + (21.40/3600)); // = -94.622611
          console.log('Manual longitude conversion:', lng);
        }
      }
    }
    
    const link = `https://www.google.com/maps?q=${lat},${lng}`;
    console.log('Generated Google Maps link:', link);
    console.log('Using coordinates:', { lat, lng });
    return link;
  };

  return (
    <div className="app-container">
      <Analytics />
      
      {/* "Authenticating" message - Full screen overlay (only if Clerk is loaded) */}
      {showAuthenticating && isLoaded && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(249, 250, 251, 0.95)',
          zIndex: 9999,
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div style={{
            padding: '1.5rem 2.5rem',
            backgroundColor: '#3b82f6',
            color: 'white',
            borderRadius: '0.75rem',
            fontSize: '1rem',
            fontWeight: '500',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            minWidth: '220px',
            justifyContent: 'center'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: '2px solid white',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              display: 'inline-block',
              flexShrink: 0
            }}></div>
            <span>Authenticating...</span>
          </div>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* "Authenticated" message - Top right notification */}
      {showAuthenticated && (
        <div style={{
          position: 'fixed',
          top: '1.5rem',
          right: '1.5rem',
          padding: '0.75rem 1.25rem',
          backgroundColor: '#10b981',
          color: 'white',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          fontWeight: '500',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          animation: 'slideIn 0.3s ease-out, fadeOut 0.3s ease-in 2.2s forwards'
        }}>
          <span style={{ fontSize: '1.125rem' }}>✓</span>
          <span>Authenticated</span>
          <style>{`
            @keyframes slideIn {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
            @keyframes fadeOut {
              from { opacity: 1; }
              to { opacity: 0; }
            }
          `}</style>
        </div>
      )}

      {/* Fallback: Show login if Clerk takes too long to load */}
      {!isLoaded && showAuthenticating && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(249, 250, 251, 0.95)',
          zIndex: 9999,
          flexDirection: 'column',
          gap: '2rem'
        }}>
          <div style={{
            padding: '1.5rem 2.5rem',
            backgroundColor: '#3b82f6',
            color: 'white',
            borderRadius: '0.75rem',
            fontSize: '1rem',
            fontWeight: '500',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            minWidth: '220px',
            justifyContent: 'center'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: '2px solid white',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              display: 'inline-block',
              flexShrink: 0
            }}></div>
            <span>Authenticating...</span>
          </div>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Main content - only shows after authentication check is complete */}
      {isLoaded && !showAuthenticating && (
        <>
          {/* Header with Auth Buttons */}
          <div className="app-header" style={{ position: 'relative', paddingBottom: '2rem' }}>
            {/* Auth buttons in top-right corner */}
            <div style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              zIndex: 100
            }}>
              {isSignedIn ? (
                <>
                  {!subscriptionLoading && (
                    <SubscriptionBadge tier={tier} size="small" />
                  )}
                  <UserButton afterSignOutUrl="/" />
                </>
              ) : (
                <>
                  <SignInButton mode="modal">
                    <button style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '600',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#2563eb';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#3b82f6';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                    }}>
                      Sign In
                    </button>
                  </SignInButton>
                  <SignUpButton mode="modal">
                    <button style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '600',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#059669';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#10b981';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                    }}>
                      Sign Up
                    </button>
                  </SignUpButton>
                </>
              )}
            </div>
            <h1>Image Metadata Extractor</h1>
            <p>
              Upload an image to extract and explore its metadata. We'll show you the key details first, 
              with options to dive deeper into the technical information.
            </p>
          </div>

          {/* Prominent Login Section - Only show when NOT signed in AND no metadata uploaded yet */}
          {!isSignedIn && !metadata && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                backgroundColor: '#f0f9ff',
                border: '2px solid #3b82f6',
                borderRadius: '1rem',
                padding: '2rem',
                margin: '2rem auto',
                maxWidth: '600px',
                textAlign: 'center'
              }}
            >
              <h2 style={{ marginTop: 0, marginBottom: '1rem', color: '#1e40af' }}>
                Sign In to Get Started
              </h2>
              <p style={{ marginBottom: '1.5rem', color: '#475569' }}>
                Create an account or sign in to upload and analyze images
              </p>
              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                <SignInButton mode="modal">
                  <button style={{
                    padding: '1rem 2rem',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease',
                    minWidth: '150px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2563eb';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}>
                    Sign In
                  </button>
                </SignInButton>
                <SignUpButton mode="modal">
                  <button style={{
                    padding: '1rem 2rem',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease',
                    minWidth: '150px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#059669';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#10b981';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}>
                    Sign Up
                  </button>
                </SignUpButton>
              </div>
            </motion.div>
          )}


        {!metadata ? (
        /* Upload Screen */
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="upload-container"
        >
          <div
            {...getRootProps()}
            className={`upload-area ${isDragActive ? 'dragover' : ''}`}
          >
            <input {...getInputProps()} />
            <div className="upload-icon">
              <UploadIcon />
            </div>
            <h2 className="upload-text">
              {isDragActive ? 'Drop your image here' : 'Upload an Image'}
            </h2>
            <p className="upload-subtext">
              Drag and drop or click to select
            </p>
            <p className="upload-note">
              Supports JPEG, PNG, GIF, BMP, TIFF, WebP
            </p>
            <p className="upload-note" style={{ marginTop: '16px' }}>
              We'll extract metadata — you choose what to see
            </p>
          </div>

          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="apple-card"
              style={{ textAlign: 'center' }}
            >
              <div className="loading">
                <div className="spinner"></div>
                <span>Processing image...</span>
              </div>
            </motion.div>
          )}

          {/* Account information - below Upload an Image when signed in */}
          {isSignedIn && !subscriptionLoading && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '0.75rem',
                padding: '1rem 1.5rem',
                margin: '2rem auto 2rem',
                maxWidth: '600px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <SubscriptionBadge tier={tier} size="medium" />
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {subscriptionInfo.displayName} Plan
                    </span>
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                    {subscriptionInfo.description}
                  </p>
                </div>
                <div style={{
                  textAlign: 'right',
                  padding: '0.75rem 1rem',
                  backgroundColor: remainingUploads > 0 ? '#dbeafe' : '#fee2e2',
                  borderRadius: '0.5rem',
                  border: `1px solid ${remainingUploads > 0 ? '#3b82f6' : '#ef4444'}`
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Uploads this hour
                  </div>
                  <div style={{
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    color: remainingUploads > 0 ? '#1e40af' : '#dc2626'
                  }}>
                    {uploadCount} / {getUploadLimit(tier)}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: remainingUploads > 0 ? '#059669' : '#dc2626',
                    marginTop: '0.25rem',
                    fontWeight: '600'
                  }}>
                    {remainingUploads > 0 ? `${remainingUploads} remaining` : 'Limit reached'}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => {
                    // Reorder pages so Billing appears first (and is the default view)
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Clerk customPages accepts built-in labels at runtime
                    clerk?.openUserProfile?.({
                      customPages: [
                        { label: 'billing' },
                        { label: 'account' },
                        { label: 'security' },
                      ],
                    } as any);
                  }}
                  style={{
                    display: 'inline-block',
                    padding: '0.5rem 1.25rem',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 3px rgba(59, 130, 246, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2563eb';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Upgrade Account
                </button>
              </div>
            </motion.div>
          )}

        </motion.div>
      ) : (
        /* Results Screen - Show sign-up prompt if not authenticated, otherwise show results */
        /* Only show results if Clerk is loaded AND user is definitely signed in */
        /* Otherwise show sign-up prompt */
        (isLoaded && isSignedIn === true) ? (
          /* Results Screen - Show metadata for authenticated users */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="results-container"
          >
            {/* Hero Card */}
            <div className="hero-card">
              <div className="hero-content">
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="hero-image"
                  />
                )}
                <div className="hero-info">
                  <h2 className="hero-title">
                    {filename}
                  </h2>
                  <div className="hero-details">
                    <div className="hero-detail">
                      <span className="hero-detail-label">Format</span>
                      <p className="hero-detail-value">
                        {metadata.MIMEType?.toString().split('/')[1].toUpperCase() || 'Unknown'}
                      </p>
                    </div>
                    <div className="hero-detail">
                      <span className="hero-detail-label">Size</span>
                      <p className="hero-detail-value">
                        {metadata.FileSize || 'Unknown'}
                      </p>
                    </div>
                    <div className="hero-detail">
                      <span className="hero-detail-label">Dimensions</span>
                      <p className="hero-detail-value">
                        {metadata.ImageWidth} × {metadata.ImageHeight}
                      </p>
                    </div>
                    <div className="hero-detail">
                      <span className="hero-detail-label">Captured</span>
                      <p className="hero-detail-value">
                        {formatDate(metadata.DateTimeOriginal?.toString() || '')}
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={clearResults}
                  className="apple-button-secondary"
                >
                  Upload Another
                </button>
              </div>
            </div>

            {/* Primary Metadata Cards */}
            <div className="metadata-grid">
              {/* Camera & Settings */}
              <MetadataCard title="Camera & Settings" icon={<CameraIcon />}>
                <div className="metadata-list">
                  <div className="metadata-item">
                    <span className="metadata-key">Camera</span>
                    <span className="metadata-value">
                      {metadata.Make} {metadata.Model}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-key">Lens</span>
                    <span className="metadata-value">
                      {metadata.LensModel || 'Unknown'}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-key">Aperture</span>
                    <span className="metadata-value">
                      f/{metadata.FNumber}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-key">Shutter Speed</span>
                    <span className="metadata-value">
                      {metadata.ExposureTime}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-key">ISO</span>
                    <span className="metadata-value">
                      {metadata.ISO}
                    </span>
                  </div>
                </div>
              </MetadataCard>

              {/* Location */}
              {hasLocationData && (
                <MetadataCard title="Location" icon={<LocationIcon />}>
                  <div className="metadata-list">
                    {metadata.GPSLatitude && metadata.GPSLongitude && (
                      <div className="location-privacy">
                        <div className="location-toggle">
                          <span className="location-toggle-label">GPS Coordinates</span>
                          <button
                            onClick={() => {
                              setShowLocation(!showLocation);
                              track('location_toggle', {
                                action: showLocation ? 'hide' : 'show',
                                has_gps_data: !!(metadata.GPSLatitude && metadata.GPSLongitude)
                              });
                            }}
                            className="location-toggle-button"
                          >
                            {showLocation ? 'Hide' : 'Show'}
                          </button>
                        </div>
                        {showLocation ? (
                          <div className="location-data">
                            <div className="gps-coordinates">
                              {(() => {
                                const mapsLink = getGoogleMapsLink();
                                return mapsLink ? (
                                  <a 
                                    href={mapsLink} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ 
                                      color: '#007AFF', 
                                      textDecoration: 'none',
                                      display: 'block',
                                      padding: '12px',
                                      borderRadius: '8px',
                                      backgroundColor: '#f0f8ff',
                                      border: '1px solid #007AFF',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#e6f3ff';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = '#f0f8ff';
                                    }}
                                  >
                                    <div style={{ fontWeight: '600', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      📍 GPS Coordinates
                                    </div>
                                    {convertedCoordinates ? (
                                      <div style={{ fontSize: '16px', fontFamily: 'monospace', color: '#374151' }}>
                                        {convertedCoordinates.lat.toFixed(6)}, {convertedCoordinates.lng.toFixed(6)}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: '14px', color: '#374151' }}>
                                        {metadata.GPSLatitude}, {metadata.GPSLongitude}
                                      </div>
                                    )}
                                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                                      Click to view on Google Maps
                                    </div>
                                  </a>
                                ) : (
                                  <div style={{ padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #e5e5e5' }}>
                                    <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                                      📍 GPS Coordinates
                                    </div>
                                    {convertedCoordinates ? (
                                      <div style={{ fontSize: '16px', fontFamily: 'monospace', color: '#374151' }}>
                                        {convertedCoordinates.lat.toFixed(6)}, {convertedCoordinates.lng.toFixed(6)}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: '14px', color: '#374151' }}>
                                        {metadata.GPSLatitude}, {metadata.GPSLongitude}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                            {metadata.GPSAltitude && (
                              <div style={{ marginTop: '8px', fontSize: '14px' }}>
                                Altitude: {metadata.GPSAltitude}m
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="location-hidden">
                            Location data hidden for privacy
                          </div>
                        )}
                      </div>
                    )}
                    <div className="metadata-item">
                      <span className="metadata-key">Coordinates</span>
                      <span className="metadata-value">
                        {getLocationDisplay()}
                      </span>
                    </div>
                    <div className="metadata-item">
                      <span className="metadata-key">Location</span>
                      <span className="metadata-value">
                        {getLocationDetails()}
                      </span>
                    </div>
                  </div>
                </MetadataCard>
              )}

              {/* Time Captured */}
              <MetadataCard title="Time Captured" icon={<TimeIcon />}>
                <div className="metadata-list">
                  <div className="metadata-item">
                    <span className="metadata-key">Date</span>
                    <span className="metadata-value">
                      {formatDate(metadata.DateTimeOriginal?.toString() || '')}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-key">Timezone</span>
                    <span className="metadata-value">
                      Local Time
                    </span>
                  </div>
                </div>
              </MetadataCard>

              {/* Quick Quality */}
              <MetadataCard title="Quick Quality" icon={<QualityIcon />}>
                <div className="metadata-list">
                  <div className="metadata-item">
                    <span className="metadata-key">Resolution</span>
                    <span className="metadata-value">
                      {metadata.ImageWidth} × {metadata.ImageHeight}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-key">Orientation</span>
                    <span className="metadata-value">
                      {metadata.Orientation === '1' ? 'Landscape' : 'Portrait'}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-key">Color Profile</span>
                    <span className="metadata-value">
                      {metadata.ColorSpace || 'sRGB'}
                    </span>
                  </div>
                </div>
              </MetadataCard>
            </div>

            {/* Expandable Sections */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Accordion title="Advanced Camera Data" icon={<CameraIcon />}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', fontSize: '0.875rem' }}>
                  <div className="metadata-item">
                    <span className="metadata-key">Metering Mode</span>
                    <span className="metadata-value">{metadata.MeteringMode}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-key">Exposure Mode</span>
                    <span className="metadata-value">{metadata.ExposureMode}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-key">Focus Mode</span>
                    <span className="metadata-value">{metadata.FocusMode}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-key">Image Stabilization</span>
                    <span className="metadata-value">{metadata.ImageStabilization}</span>
                  </div>
                </div>
              </Accordion>

              <Accordion title="Device & Software" icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}>
                <div className="metadata-list">
                  <div className="metadata-item">
                    <span className="metadata-key">Software</span>
                    <span className="metadata-value">{metadata.Software}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-key">Copyright</span>
                    <span className="metadata-value">{metadata.Copyright || 'None'}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-key">Artist</span>
                    <span className="metadata-value">{metadata.Artist || 'Unknown'}</span>
                  </div>
                </div>
              </Accordion>

              <Accordion title="Color & Light" icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" /></svg>}>
                <div className="metadata-list">
                  <div className="metadata-item">
                    <span className="metadata-key">White Balance</span>
                    <span className="metadata-value">{metadata.WhiteBalance}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-key">Flash</span>
                    <span className="metadata-value">{metadata.Flash}</span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-key">Color Profile</span>
                    <span className="metadata-value">{metadata.ColorProfile}</span>
                  </div>
                </div>
              </Accordion>
            </div>

            {/* Show All Metadata */}
            <div className="show-all-section">
              <div className="show-all-header">
                <h3 className="show-all-title">All Metadata</h3>
                <button
                  onClick={() => setShowAllMetadata(!showAllMetadata)}
                  className="apple-button-secondary"
                >
                  {showAllMetadata ? 'Hide' : 'Show All'}
                </button>
              </div>
              
              <AnimatePresence>
                {showAllMetadata && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="accordion-content open"
                  >
                    <div className="metadata-table">
                      <div className="metadata-table-grid">
                        {Object.entries(metadata).map(([key, value]) => (
                          <div key={key} className="metadata-table-item">
                            <span className="metadata-table-key">{key}</span>
                            <span className="metadata-table-value">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="show-all-actions">
                      <button
                        onClick={() => navigator.clipboard.writeText(JSON.stringify(metadata, null, 2))}
                        className="apple-button-secondary"
                      >
                        Copy JSON
                      </button>
                      <button
                        onClick={() => {
                          const blob = new Blob([JSON.stringify(metadata, null, 2)], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${filename?.split('.')[0] || 'metadata'}.json`;
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                        className="apple-button-secondary"
                      >
                        Download JSON
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        ) : (
          /* Teaser results + sign-up prompt for unauthenticated users */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="results-container"
          >
            {/* Teaser Hero Card */}
            <div className="hero-card">
              <div className="hero-content">
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="hero-image"
                  />
                )}
                <div className="hero-info">
                  <h2 className="hero-title">
                    {filename}
                  </h2>
                  <div className="hero-details">
                    <div className="hero-detail">
                      <span className="hero-detail-label">Format</span>
                      <p className="hero-detail-value">******</p>
                    </div>
                    <div className="hero-detail">
                      <span className="hero-detail-label">Size</span>
                      <p className="hero-detail-value">******</p>
                    </div>
                    <div className="hero-detail">
                      <span className="hero-detail-label">Dimensions</span>
                      <p className="hero-detail-value">******</p>
                    </div>
                    <div className="hero-detail">
                      <span className="hero-detail-label">Captured</span>
                      <p className="hero-detail-value">******</p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={clearResults}
                  className="apple-button-secondary"
                >
                  Upload Another
                </button>
              </div>
            </div>

            <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
              <h2 style={{ fontSize: '1.75rem', fontWeight: '600', marginBottom: '0.5rem', color: '#1d1d1f' }}>
                Sign up to reveal your full image metadata
              </h2>
              <p style={{ fontSize: '1rem', color: '#6e6e73', marginBottom: '2.5rem', maxWidth: '600px', margin: '0 auto 2.5rem' }}>
                Including camera settings, location data, and more.
              </p>

              {/* Subscription Tiers Comparison */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
                gap: '1.25rem', 
                maxWidth: '1000px', 
                margin: '0 auto 3rem',
                alignItems: 'stretch'
              }}>
                {/* Free Tier */}
                <SignUpButton mode="modal">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    style={{
                      backgroundColor: '#ffffff',
                      border: '2px solid #e5e7eb',
                      borderRadius: '1rem',
                      padding: '1.5rem 1.25rem',
                      textAlign: 'left',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#9ca3af';
                      e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)';
                      e.currentTarget.style.transform = 'translateY(-4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ marginBottom: '0.75rem' }}>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.375rem', color: '#1d1d1f' }}>
                        {SUBSCRIPTION_TIERS.free.displayName}
                      </h3>
                      <p style={{ fontSize: '0.8125rem', color: '#6e6e73', marginBottom: '0' }}>
                        {SUBSCRIPTION_TIERS.free.description}
                      </p>
                    </div>
                    <div style={{ marginBottom: '1rem', flexGrow: 1 }}>
                      <div style={{ fontSize: '0.8125rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                        Upload Limit: {getUploadLimit('free')} per hour
                      </div>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {SUBSCRIPTION_TIERS.free.features.map((feature, idx) => (
                          <li key={idx} style={{ 
                            fontSize: '0.8125rem', 
                            color: '#6e6e73', 
                            marginBottom: '0.375rem',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.5rem'
                          }}>
                            <span style={{ color: '#10b981', fontSize: '0.875rem' }}>✓</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div style={{ marginTop: 'auto' }}>
                      <div
                        style={{
                          width: '100%',
                          maxWidth: '100%',
                          padding: '0.75rem 1rem',
                          fontSize: '0.9375rem',
                          fontWeight: '600',
                          backgroundColor: '#f3f4f6',
                          color: '#374151',
                          border: '2px solid #e5e7eb',
                          borderRadius: '0.5rem',
                          textAlign: 'center',
                          transition: 'all 0.2s',
                          boxSizing: 'border-box'
                        }}
                      >
                        Get Started
                      </div>
                    </div>
                  </motion.div>
                </SignUpButton>

                {/* Personal Tier */}
                <SignUpButton mode="modal">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                    style={{
                      backgroundColor: '#ffffff',
                      border: '2px solid #3b82f6',
                      borderRadius: '1rem',
                      padding: '1.5rem 1.25rem',
                      textAlign: 'left',
                      position: 'relative',
                      boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.1)',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#2563eb';
                      e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(59, 130, 246, 0.3), 0 8px 10px -6px rgba(59, 130, 246, 0.2)';
                      e.currentTarget.style.transform = 'translateY(-4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#3b82f6';
                      e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(59, 130, 246, 0.1)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ 
                      position: 'absolute', 
                      top: '-12px', 
                      left: '50%', 
                      transform: 'translateX(-50%)',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '1rem',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      POPULAR
                    </div>
                    <div style={{ marginBottom: '0.75rem', marginTop: '0.5rem' }}>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.375rem', color: '#3b82f6' }}>
                        {SUBSCRIPTION_TIERS.personal.displayName}
                      </h3>
                      <p style={{ fontSize: '0.8125rem', color: '#6e6e73', marginBottom: '0' }}>
                        {SUBSCRIPTION_TIERS.personal.description}
                      </p>
                    </div>
                    <div style={{ marginBottom: '1rem', flexGrow: 1 }}>
                      <div style={{ fontSize: '0.8125rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                        Upload Limit: {getUploadLimit('personal')} per hour
                      </div>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {SUBSCRIPTION_TIERS.personal.features.map((feature, idx) => (
                          <li key={idx} style={{ 
                            fontSize: '0.8125rem', 
                            color: '#6e6e73', 
                            marginBottom: '0.375rem',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.5rem'
                          }}>
                            <span style={{ color: '#10b981', fontSize: '0.875rem' }}>✓</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div style={{ marginTop: 'auto' }}>
                      <div
                        style={{
                          width: '100%',
                          maxWidth: '100%',
                          padding: '0.75rem 1rem',
                          fontSize: '0.9375rem',
                          fontWeight: '600',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.5rem',
                          textAlign: 'center',
                          transition: 'all 0.2s',
                          boxSizing: 'border-box'
                        }}
                      >
                        Choose Personal
                      </div>
                    </div>
                  </motion.div>
                </SignUpButton>

                {/* Professional Tier */}
                <SignUpButton mode="modal">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    style={{
                      backgroundColor: '#ffffff',
                      border: '2px solid #10b981',
                      borderRadius: '1rem',
                      padding: '1.5rem 1.25rem',
                      textAlign: 'left',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#059669';
                      e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(16, 185, 129, 0.3), 0 8px 10px -6px rgba(16, 185, 129, 0.2)';
                      e.currentTarget.style.transform = 'translateY(-4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#10b981';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ marginBottom: '0.75rem' }}>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.375rem', color: '#10b981' }}>
                        {SUBSCRIPTION_TIERS.professional.displayName}
                      </h3>
                      <p style={{ fontSize: '0.8125rem', color: '#6e6e73', marginBottom: '0' }}>
                        {SUBSCRIPTION_TIERS.professional.description}
                      </p>
                    </div>
                    <div style={{ marginBottom: '1rem', flexGrow: 1 }}>
                      <div style={{ fontSize: '0.8125rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                        Upload Limit: {getUploadLimit('professional')} per hour
                      </div>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {SUBSCRIPTION_TIERS.professional.features.map((feature, idx) => (
                          <li key={idx} style={{ 
                            fontSize: '0.8125rem', 
                            color: '#6e6e73', 
                            marginBottom: '0.375rem',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.5rem'
                          }}>
                            <span style={{ color: '#10b981', fontSize: '0.875rem' }}>✓</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div style={{ marginTop: 'auto' }}>
                      <div
                        style={{
                          width: '100%',
                          maxWidth: '100%',
                          padding: '0.75rem 1rem',
                          fontSize: '0.9375rem',
                          fontWeight: '600',
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.5rem',
                          textAlign: 'center',
                          transition: 'all 0.2s',
                          boxSizing: 'border-box'
                        }}
                      >
                        Choose Professional
                      </div>
                    </div>
                  </motion.div>
                </SignUpButton>
              </div>

              {/* Sign In Option */}
              <div style={{ textAlign: 'center', marginTop: '2rem', paddingTop: '2rem' }}>
                <p style={{ fontSize: '0.875rem', color: '#86868b', marginBottom: '0.75rem' }}>
                  Already have an account?
                </p>
                <SignInButton mode="modal">
                  <button
                    style={{
                      padding: '0.75rem 2rem',
                      fontSize: '1rem',
                      fontWeight: '500',
                      backgroundColor: 'transparent',
                      color: '#0071e3',
                      border: '1px solid #0071e3',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f7';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Sign In
                  </button>
                </SignInButton>
              </div>
            </div>
          </motion.div>
        )
      )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="error-card"
          >
            <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="error-content">
              <h3>Error</h3>
              <p>{error}</p>
            </div>
          </motion.div>
        )}

        {/* Debug Information - Only show for authenticated users */}
        {metadata && isLoaded && isSignedIn === true && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="debug-section"
          >
            <div className="debug-header">
              <h3 className="debug-title">Debug Information</h3>
              <p className="debug-subtitle">Raw metadata and EXIF data extracted from the image</p>
              <button
                onClick={() => {
                  setShowDebugInfo(!showDebugInfo);
                  track('debug_toggle', {
                    action: showDebugInfo ? 'hide' : 'show'
                  });
                }}
                className="apple-button-secondary"
              >
                {showDebugInfo ? 'Hide Debug Info' : 'Show Debug Info'}
              </button>
            </div>
            
            <AnimatePresence>
              {showDebugInfo && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="debug-content"
                >
                  <div className="debug-info-grid">
                    <div className="debug-info-item">
                      <h4>Raw Metadata (JSON)</h4>
                      <pre className="debug-json">
                        {JSON.stringify(metadata, null, 2)}
                      </pre>
                    </div>
                    
                    {convertedCoordinates && (
                      <div className="debug-info-item">
                        <h4>Converted GPS Coordinates</h4>
                        <pre className="debug-json">
                          {JSON.stringify(convertedCoordinates, null, 2)}
                        </pre>
                      </div>
                    )}
                    
                    {geocodedLocation && (
                      <div className="debug-info-item">
                        <h4>Geocoded Location Data</h4>
                        <pre className="debug-json">
                          {JSON.stringify(geocodedLocation, null, 2)}
                        </pre>
                      </div>
                    )}
                    
                    <div className="debug-info-item">
                      <h4>Technical Details</h4>
                      <div className="debug-details">
                        <div className="debug-detail-item">
                          <span className="debug-detail-label">Total Metadata Fields:</span>
                          <span className="debug-detail-value">{Object.keys(metadata).length}</span>
                        </div>
                        <div className="debug-detail-item">
                          <span className="debug-detail-label">Has GPS Data:</span>
                          <span className="debug-detail-value">
                            {metadata.GPSLatitude && metadata.GPSLongitude ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="debug-detail-item">
                          <span className="debug-detail-label">Has Camera Info:</span>
                          <span className="debug-detail-value">
                            {metadata.Make || metadata.Model ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="debug-detail-item">
                          <span className="debug-detail-label">Has Date Info:</span>
                          <span className="debug-detail-value">
                            {metadata.DateTime || metadata.DateTimeOriginal ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="debug-detail-item">
                          <span className="debug-detail-label">Geocoding Status:</span>
                          <span className="debug-detail-value">
                            {geocodingLoading ? 'Loading...' : geocodedLocation ? 'Completed' : 'Not attempted'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="debug-actions">
                    <button
                      onClick={() => navigator.clipboard.writeText(JSON.stringify(metadata, null, 2))}
                      className="apple-button-secondary"
                    >
                      Copy Raw JSON
                    </button>
                    <button
                      onClick={() => {
                        const debugData = {
                          metadata,
                          convertedCoordinates,
                          geocodedLocation,
                          timestamp: new Date().toISOString(),
                          filename: filename || 'unknown'
                        };
                        const blob = new Blob([JSON.stringify(debugData, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${filename?.split('.')[0] || 'debug'}_metadata.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="apple-button-secondary"
                    >
                      Download Debug Data
                    </button>
                    <button
                      onClick={testCoordinateConversion}
                      className="apple-button-secondary"
                    >
                      Test Coordinate Conversion
                    </button>
                    <button
                      onClick={testGeocodingService}
                      className="apple-button-secondary"
                    >
                      Test Geocoding Service
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </>
      )}
    </div>
  );
};

export default App;
